import {
    useRef,
    forwardRef,
    ForwardedRef,
    useImperativeHandle,
    useMemo,
    useEffect,
    useLayoutEffect,
    SyntheticEvent
} from "react";
import {useSelector} from "react-redux";
import {AppState} from "../state";
import equals from "fast-deep-equal";
import type Hls from "hls.js";

export type PlayerElementHandle = {
    play(): Promise<void>
    pause(): void
    load(): void
    get currentTime(): number
    get duration(): number
    get readyState(): number
    get error(): MediaError | undefined | null
}

export type Src = {
    type?: string
    src: string
}

const PlayerElementInner = (props: React.VideoHTMLAttributes<HTMLVideoElement> & { volume?: number, sources: Src[], position?: number, speed?: number }, ref: ForwardedRef<PlayerElementHandle>) => {
    const vRef = useRef<HTMLVideoElement>(null)
    const vRef2 = useRef<HTMLVideoElement>(null);
    const {id, volumeState, positionState, speedState} = useSelector((appState: AppState) => ({id: appState.id, volumeState: appState.volume, positionState: appState.position, speedState: appState.speed}), equals);
    const handle = useMemo<PlayerElementHandle>(() => {
        return {
            play: () => vRef.current?.play() || Promise.reject(),
            pause: () => vRef.current?.pause(),
            load: () => vRef.current?.load(),
            get currentTime(): number {
                return vRef.current?.currentTime || 0;
            },
            get duration(): number {
                return vRef.current?.duration || 0;
            },
            get readyState(): number {
                return vRef.current?.readyState || 0;
            },
            get error(): MediaError | undefined | null {
                return vRef.current?.error;
            }
        }
    }, []);

    const volume = props.volume !== undefined ? props.volume : volumeState;
    const position = props.position !== undefined ? props.position : positionState;
    const speed = props.speed !== undefined ? props.speed : speedState;

    useImperativeHandle(ref, () => handle)

    const metadataLoaded = useRef(false);

    const mediastream = useMemo(() => {
        if (props.sources.length
            && (props.sources[0].src.indexOf(".m3u8") > -1 ||
                props.sources[0].type === 'dash'))
        {
            return true;
        }
        return false;
    }, [props.sources.map(x => x.src).join(',')]);

    useLayoutEffect(() => {
        if (!mediastream)
            return undefined;

        const player = vRef.current;

        if (!player)
            return  undefined;

        let turnOf = false;
        let worker: Hls|null = null;

        import("hls.js").then(x => {
            if (turnOf)
                return;

            worker = new x.default();
            worker.attachMedia(player);
            worker.loadSource(props.sources[0].src);
        });

        return () => {
            turnOf = true;
            if (worker) {
                worker.detachMedia();
                worker.destroy();
            }
        }

    }, [mediastream]);



    useLayoutEffect(() => {

        if (vRef.current) {
            metadataLoaded.current = false;
            const player = vRef.current;
            const metadataloaded = () => {
                metadataLoaded.current = true;
                player.removeEventListener("loadedmetadata", metadataloaded);
            };

            player.addEventListener("loadedmetadata", metadataloaded);
            player.load();
            player.volume = volume;
            player.playbackRate = speed;
            player.play();
            return () => player.removeEventListener("loadedmetadata", metadataloaded);

        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(props.sources)]);

    useLayoutEffect(() => {
        if (vRef.current) {
            vRef.current.volume = volume;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [volume]);

    useLayoutEffect(() => {
        if (vRef.current) {
            vRef.current.playbackRate = speed;
        }
    }, [speed]);

    useLayoutEffect(() => {
        if (vRef.current) {
            const player = vRef.current;
            if (metadataLoaded.current) {

                if (position > (player.currentTime + 1) || position < (player.currentTime - 1)) {
                    player.currentTime = position;
                }
            } else {
                const onLoad = () => {
                    player.currentTime = position;
                    player.removeEventListener("loadedmetadata", onLoad);
                }
                player.addEventListener("loadedmetadata", onLoad);
                return () => {
                    player.removeEventListener("loadedmetadata", onLoad);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [position, JSON.stringify(props.sources)]);


    const rIntervalUpdate = useRef<ReturnType<typeof setInterval>>()
    const onTimeUpdate = props.onTimeUpdate;
    useEffect(() => {
        if (!onTimeUpdate && rIntervalUpdate.current) {
            rIntervalUpdate.current = undefined
            clearInterval(rIntervalUpdate.current);
        } else if (onTimeUpdate && !rIntervalUpdate.current) {
            rIntervalUpdate.current = setInterval(() => {
                if (vRef.current && onTimeUpdate) {
                    const curTime = vRef.current.currentTime;
                    if (curTime) {
                        onTimeUpdate({
                            timeStamp: curTime
                        } as SyntheticEvent<HTMLVideoElement>)
                    }
                }
            }, 1000);
        }
    }, [onTimeUpdate]);

    useEffect(() => {

        // @ts-ignore
        vRef2.current = vRef.current
        return () => {
            if (rIntervalUpdate.current) {
                clearInterval(rIntervalUpdate.current);
            }
            const audioTag = vRef2.current;

            if (audioTag) {
                audioTag.addEventListener('pause', (ev) => {
                    const sources = audioTag.getElementsByTagName('source');
                    for (let i = 0; i < sources.length; i++) {
                        audioTag.removeChild(sources[i]);
                    }
                    audioTag.src = '';
                    try {
                        audioTag.load();
                        audioTag.currentTime = 0;
                    } catch (e) {

                    }
                });
                audioTag.pause();
            }
        }
    }, []);

    const newProps = {...props};
    delete newProps.onTimeUpdate;
    delete newProps.children;
    // @ts-ignore-next-line
    delete newProps.sources;

    newProps.onTimeUpdate = (e) => {
        if (rIntervalUpdate.current)
            clearInterval(rIntervalUpdate.current);
        if (props.onTimeUpdate && metadataLoaded.current)
            props.onTimeUpdate(e);
    }

    return <video id={'videoplayerradiacz-' + id} width={20} height={2} disablePictureInPicture preload={'metadata'} ref={vRef}
                  playsInline={true} {...newProps}>
        { !mediastream ?
         props.sources.map((i, k) => <source {...i} key={`${i.src}${i.type}`}/>) : null}
    </video>
}

export const PlayerElement = forwardRef(PlayerElementInner)